import { useQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import React, { FC, useContext, useEffect } from 'react';
import { useDeepCompareEffect } from 'react-use';

import HeadingText from '@ui/atoms/HeadingText';
import Skeleton from '@ui/atoms/Skeleton';
import Toast from '@ui/atoms/Toast';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import SpacedGridItem from '@ui/molecules/SpacedGridItem';

import useMemberInfo from '@hooks/useMemberInfo';

import { showAppointmentToast } from '@helpers/Appointments/AppointmentHelpers';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { FlowIdTypes } from '@folx-types/flow';

import NewAppointmentBooking, { ServiceDuration } from '@components/NewAppointmentBooking';

import CURRENT_MEMBER_WITH_DEFAULT_VISIT_DURATION from '@graphql/queries/currentMemberWithDefaultVisitDuration.gql';

export interface ISelectAppointmentStepContent {
	appointmentSelectionHeaderDescription: string;
}

interface ISelectAppointmentStepProps {
	refinedContent: ISelectAppointmentStepContent;
	serviceType?: FlowIdTypes;
}

const SelectAppointmentStep: FC<ISelectAppointmentStepProps> = ({
	refinedContent,
	serviceType,
}) => {
	const { appointmentSelectionHeaderDescription } = refinedContent;

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);

	const {
		loading: serviceDurationLoading,
		error: serviceDurationError,
		data: serviceDurationValue,
	} = useQuery<{ authenticatedMember: { defaultVisitDuration: ServiceDuration } }>(
		CURRENT_MEMBER_WITH_DEFAULT_VISIT_DURATION,
		{
			variables: {
				serviceType: serviceType?.toUpperCase(),
			},
			client: graphQLv2Client,
		}
	);

	const { memberInfo } = useMemberInfo();

	useDeepCompareEffect(() => {
		if (serviceDurationError) {
			datadogRum.addError(
				`Error getting default visit duration: ${JSON.stringify({
					error: serviceDurationError,
				})}`
			);
		}
	}, [serviceDurationError]);

	useEffect(() => {
		showAppointmentToast(memberInfo?.stateOfResidence?.abbreviation);
	}, [memberInfo?.stateOfResidence?.abbreviation]);

	if (serviceDurationLoading || !serviceDurationValue) {
		return <Skeleton fullHeight fullWidth structure="single" />;
	}

	return (
		<CenteredGridItem>
			<SpacedGridItem marginBottom="sm">
				<HeadingText>{appointmentSelectionHeaderDescription}</HeadingText>
			</SpacedGridItem>
			<SpacedGridItem marginBottom="xl">
				<NewAppointmentBooking
					defaultVisitDuration={serviceDurationValue?.authenticatedMember?.defaultVisitDuration}
					serviceType={serviceType}
					serviceDuration={serviceDurationValue?.authenticatedMember?.defaultVisitDuration}
					serviceVersion={1}
				/>
			</SpacedGridItem>
			<Toast
				position="top-center"
				theme="colored"
				pauseOnHover
				pauseOnFocusLoss={false}
				autoClose={7500}
			/>
		</CenteredGridItem>
	);
};

export default SelectAppointmentStep;
